import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, ArrowRight } from 'lucide-react';

/**
 * Low-friction "Continue as Guest" form: name, email, phone — no password.
 * On success, calls onSuccess(guestUser) so callers can navigate/redirect.
 */
const GuestLoginModal = ({ open, onOpenChange, onSuccess }) => {
  const { guestLogin } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedName || !trimmedEmail || !trimmedPhone) {
      setError('Name, email, and phone number are all required to continue as a guest.');
      return;
    }

    setLoading(true);
    try {
      const guestUser = await guestLogin({ name: trimmedName, email: trimmedEmail, phone: trimmedPhone });
      onOpenChange(false);
      onSuccess?.(guestUser);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not continue as guest. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Continue as Guest</DialogTitle>
          <DialogDescription>
            Just a few details so we can send your booking confirmation. You can create a full account later to save your booking history.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="guest-name">
              Full name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="guest-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              required
              minLength={2}
              aria-required="true"
              data-testid="guest-login-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="guest-email">
              Email address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="guest-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              required
              aria-required="true"
              data-testid="guest-login-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="guest-phone">
              Phone number <span className="text-destructive">*</span>
            </Label>
            <Input
              id="guest-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              required
              minLength={7}
              aria-required="true"
              data-testid="guest-login-phone-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            disabled={loading}
            data-testid="guest-login-submit-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                Continue as Guest
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GuestLoginModal;
